//import "./FooterForm.scss";
import { ContactsComponent_Plain } from "../../types/components/page/ContactsComponent.ts";
import { LinWithIconComponent_Plain } from "../../types/components/shared/LinWithIconComponent.ts";
import { getStrapiMedia, sendStrapiRequest } from "../../utils/utils.tsx";
import { useState, useEffect, useRef, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../Forms/Input/Input.tsx";
import {
  company_validation,
  email_validation,
  from_hidden_validation,
  message_validation,
  name_validation,
  to_hidden_validation,
} from "../../utils/inputValidations.tsx";
import Hidden from "../Forms/Hidden/Hidden.tsx";
import { useTranslation } from "react-i18next";

const FooterFormESG = ({
  data,
  locale,
}: {
  data: ContactsComponent_Plain;
  locale: string;
}) => {
  const { t } = useTranslation();
  const socialItems = data.socials.map(
    (item: LinWithIconComponent_Plain, i: number) => (
      <li key={i}>
        <a href={item.link} target={item.target}>
          {item.icon ? (
            <img
              src={getStrapiMedia(item.icon.url)}
              width="50"
              alt={item.title}
            />
          ) : (
            ""
          )}
        </a>
      </li>
    ),
  );

  const emails = data.to.map((item) => ({
    name: item.name,
    email: item.email,
    officeName: null,
  }));

  const toItems = emails.map((item, i: number) => (
    <li
      key={i}
      onClick={() =>
        handleListItemClick(item.name, item.email, item.officeName)
      }
      data-email={item.email}
    >
      {item.name}
    </li>
  ));

  const methods = useForm({
    defaultValues: {
      to: emails[0]?.email || "",
      location: emails[0]?.name || "",
      officeName: emails[0]?.officeName || null,
    },
  });

  const [success, setSuccess] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const messageToRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);

  const onSubmit = methods.handleSubmit((data) => {
    sendStrapiRequest("/api/forms", data).then((res) => {
      console.log(res);
    });

    methods.reset();
    setSuccess(true);
  });

  const handleListItemClick = useCallback(
    (name: string, email: string, officeName: string | null = null) => {
      console.log(
        "Selected item - Name:",
        name,
        "Email:",
        email,
        "Office Name:",
        officeName,
      );
      methods.setValue("to", email);
      methods.setValue("location", name);
      methods.setValue("officeName", officeName);

      if (titleRef.current) {
        titleRef.current.textContent = name;
      }
      setIsDropdownOpen(false);
      if (dropdownRef.current) {
        setTimeout(() => setIsDropdownOpen(false), 0);
      }
    },
    [methods, titleRef, setIsDropdownOpen, dropdownRef],
  );

  useEffect(() => {
    if (messageToRef.current && isMounted.current) {
      setTimeout(() => {
        console.log("toItems length:", toItems.length);
        if (toItems.length === 1) {
          messageToRef.current!.classList.add("one-email");
          const singleItem = emails[0];
          if (singleItem) {
            handleListItemClick(
              singleItem.name,
              singleItem.email,
              singleItem.officeName,
            );
          }
        }
      }, 0);
    }
  }, [toItems.length, emails, handleListItemClick]);

  useEffect(() => {
    isMounted.current = true;
    console.log("FooterForm mounted");

    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      isMounted.current = false;
      console.log("FooterForm unmounted");
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  return (
    <div className="esg-page-contacts box-form" id="contacts">
      <div className="wrapper">
        <div className="form__left">
          <div className="form__left-top">
            <h2>{data.title1}</h2>
            <p>{data.title2}</p>
          </div>
          <div className="form__social">
            <div className="social-list">
              <ul>{socialItems}</ul>
            </div>
          </div>
        </div>
        <div className="form__right">
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => e.preventDefault()}
              noValidate
              autoComplete="off"
              className="form"
            >
              <div className="message-to" ref={messageToRef}>
                <div className="message-to__text">{t("Message to")}:</div>
                <div
                  className={`message-to__select ${
                    isDropdownOpen ? "active-select" : ""
                  }`}
                  ref={selectRef}
                >
                  <div
                    className="message-to__select-title"
                    ref={titleRef}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {methods.getValues("to")
                      ? emails.find(
                          (item) => item.email === methods.getValues("to"),
                        )?.name
                      : emails[0]?.name || t("Select an option")}{" "}
                  </div>
                  <div className="message-to__select-dropdown">
                    <ul ref={dropdownRef}>{toItems}</ul>
                  </div>
                </div>
              </div>
              {locale == "uk" ? (
                <>
                  <Hidden {...from_hidden_validation} />
                  <Hidden {...to_hidden_validation} />
                  <Input {...name_validation} placeholder={t("Your name")} />
                  <Input {...email_validation} placeholder={t("E-mail")} />
                  <Input {...company_validation} placeholder={t("Company")} />
                  <Input {...message_validation} placeholder={t("Message")} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              ) : (
                <>
                  <Hidden {...from_hidden_validation} />
                  <Hidden {...to_hidden_validation} />
                  <Input {...name_validation} placeholder={t("Your name")} />
                  <Input {...email_validation} placeholder={t("E-mail")} />
                  <Input {...company_validation} placeholder={t("Company")} />
                  <Input {...message_validation} placeholder={t("Message")} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              )}
              {success && (
                <p className="font-semibold text-green-500 mb-5 flex items-center gap-1">
                  {t("Form has been submitted successfully")}
                </p>
              )}
              <button onClick={onSubmit} className="btn-black">
                {t("Submit your message")}
              </button>
              <div className="input-check">
                <input
                  id="Privacy"
                  type="checkbox"
                  name="Privacy"
                  readOnly
                  checked
                />
                {locale == "uk" ? (
                  <label className="input-check__label">
                    <span className="input-check__icon"></span>Я даю згоду на
                    обробку моїх персональних даних з метою надання послуг та
                    взаємодії зі мною відповідно до Політики конфіденційності.
                  </label>
                ) : (
                  <label className="input-check__label">
                    <span className="input-check__icon"></span>I consent to the
                    processing of my personal data for the purpose of providing
                    services and interacting with me in accordance with the
                    Privacy Policy.
                  </label>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="form__social show-tab">
          <div className="form__social-title">{data.socialsTitle}</div>
          <div className="social-list">
            <ul>{socialItems}</ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterFormESG;
